import React from 'react';
import {createPortal} from 'react-dom';
import {
  ServiceCategory,
  ServiceCategoryId,
  ServicePriceRangeId,
  ServicePriceRangeOption,
  ServiceSortId,
  ServiceSortOption,
} from '../constants';
import styles from '../Services.module.css';

interface ServicesToolbarProps {
  categories: ServiceCategory[];
  resultSummary: string;
  selectedCategory: ServiceCategoryId;
  priceRange: ServicePriceRangeId;
  priceRangeOptions: ServicePriceRangeOption[];
  sortBy: ServiceSortId;
  sortOptions: ServiceSortOption[];
  showFilters: boolean;
  activeFilterCount: number;
  onSortChange: (sortBy: ServiceSortId) => void;
  onToggleFilters: () => void;
  onCloseFilters: () => void;
  onApplyFilters: (filters: {
    category: ServiceCategoryId;
    priceRange: ServicePriceRangeId;
  }) => void;
  onClearFilters: () => void;
}

const SortChevronIcon: React.FC = () => (
  <svg className={styles.sortChevronIcon} viewBox="0 0 20 20" aria-hidden="true">
    <path
      d="m5 7.5 5 5 5-5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const getMenuPosition = (anchorElement: HTMLElement, optionCount: number): React.CSSProperties => {
  const rect = anchorElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const scrollLeft = window.scrollX || window.pageXOffset || document.documentElement.scrollLeft || 0;
  const scrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
  const menuGap = 8;
  const viewportPadding = 12;
  const estimatedMenuHeight = Math.min(optionCount * 44 + 12, 260);
  const width = rect.width;
  const viewportLeft = Math.min(
    Math.max(viewportPadding, rect.left),
    Math.max(viewportPadding, viewportWidth - width - viewportPadding)
  );
  const shouldOpenAbove = (
    rect.bottom + menuGap + estimatedMenuHeight > viewportHeight - viewportPadding &&
    rect.top > estimatedMenuHeight + menuGap
  );
  const viewportTop = shouldOpenAbove
    ? Math.max(viewportPadding, rect.top - estimatedMenuHeight - menuGap)
    : rect.bottom + menuGap;

  return {
    left: viewportLeft + scrollLeft,
    top: viewportTop + scrollTop,
    width,
  };
};

const getFilterPanelPosition = (anchorElement: HTMLElement): React.CSSProperties => {
  const rect = anchorElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const scrollLeft = window.scrollX || window.pageXOffset || document.documentElement.scrollLeft || 0;
  const scrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
  const menuGap = 8;
  const viewportPadding = 12;
  const preferredWidth = 380;
  const width = Math.min(preferredWidth, Math.max(280, viewportWidth - viewportPadding * 2));
  const viewportLeft = Math.min(
    Math.max(viewportPadding, rect.right - width),
    Math.max(viewportPadding, viewportWidth - width - viewportPadding)
  );
  const availableBelow = viewportHeight - rect.bottom - menuGap - viewportPadding;
  const availableAbove = rect.top - menuGap - viewportPadding;
  const shouldOpenAbove = availableBelow < 280 && availableAbove > availableBelow;
  const maxHeight = Math.max(240, Math.min(520, shouldOpenAbove ? availableAbove : availableBelow));
  const viewportTop = shouldOpenAbove
    ? Math.max(viewportPadding, rect.top - maxHeight - menuGap)
    : rect.bottom + menuGap;

  return {
    left: viewportLeft + scrollLeft,
    top: viewportTop + scrollTop,
    width,
    maxHeight,
  };
};

const getCategoryLabel = (category: ServiceCategory) => (
  category.id === 'all' ? 'All Categories' : category.name
);

const iconUrl = (fileName: string) => `${process.env.PUBLIC_URL}/icons/${fileName}`;
const toolbarIconStyle = (fileName: string) => ({
  '--services-toolbar-icon-url': `url("${iconUrl(fileName)}")`,
}) as React.CSSProperties;

const ServicesToolbar: React.FC<ServicesToolbarProps> = ({
  categories,
  resultSummary,
  selectedCategory,
  priceRange,
  priceRangeOptions,
  sortBy,
  sortOptions,
  showFilters,
  activeFilterCount,
  onSortChange,
  onToggleFilters,
  onCloseFilters,
  onApplyFilters,
  onClearFilters,
}) => {
  const sortButtonId = React.useId();
  const sortMenuId = React.useId();
  const filtersButtonId = React.useId();
  const filtersPanelId = React.useId();
  const filtersPanelTitleId = React.useId();
  const sortButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const sortMenuRef = React.useRef<HTMLDivElement | null>(null);
  const filtersButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const filtersPanelRef = React.useRef<HTMLDivElement | null>(null);
  const filtersPositionFrameRef = React.useRef<number | null>(null);
  const [isSortMenuOpen, setIsSortMenuOpen] = React.useState(false);
  const [activeSortIndex, setActiveSortIndex] = React.useState(0);
  const [sortMenuStyle, setSortMenuStyle] = React.useState<React.CSSProperties | undefined>();
  const [filtersPanelStyle, setFiltersPanelStyle] = React.useState<React.CSSProperties | undefined>();
  const [draftCategory, setDraftCategory] = React.useState<ServiceCategoryId>(selectedCategory);
  const [draftPriceRange, setDraftPriceRange] = React.useState<ServicePriceRangeId>(priceRange);
  const selectedSortIndex = Math.max(0, sortOptions.findIndex((option) => option.id === sortBy));
  const selectedSortOption = sortOptions[selectedSortIndex] || sortOptions[0];
  const effectivePriceRange = selectedCategory === 'college' ? 'all' : priceRange;
  const effectiveDraftPriceRange = draftCategory === 'college' ? 'all' : draftPriceRange;
  const hasDraftChanges = (
    draftCategory !== selectedCategory ||
    effectiveDraftPriceRange !== effectivePriceRange
  );
  const hasDraftFilters = draftCategory !== 'all' || effectiveDraftPriceRange !== 'all';

  const updateSortMenuPosition = React.useCallback(() => {
    if (!sortButtonRef.current) {
      return;
    }

    setSortMenuStyle(getMenuPosition(sortButtonRef.current, sortOptions.length));
  }, [sortOptions.length]);

  const updateFiltersPanelPosition = React.useCallback(() => {
    if (!filtersButtonRef.current) {
      return;
    }

    setFiltersPanelStyle(getFilterPanelPosition(filtersButtonRef.current));
  }, []);

  const requestFiltersPanelPositionUpdate = React.useCallback(() => {
    if (filtersPositionFrameRef.current !== null) {
      return;
    }

    filtersPositionFrameRef.current = window.requestAnimationFrame(() => {
      filtersPositionFrameRef.current = null;
      updateFiltersPanelPosition();
    });
  }, [updateFiltersPanelPosition]);

  const closeSortMenu = React.useCallback((shouldFocusButton = false) => {
    setIsSortMenuOpen(false);

    if (shouldFocusButton) {
      window.requestAnimationFrame(() => sortButtonRef.current?.focus());
    }
  }, []);

  const closeFiltersAndFocusButton = React.useCallback(() => {
    onCloseFilters();
    window.requestAnimationFrame(() => filtersButtonRef.current?.focus());
  }, [onCloseFilters]);

  const openSortMenu = React.useCallback((nextActiveIndex = selectedSortIndex) => {
    if (showFilters) {
      onCloseFilters();
    }

    if (sortButtonRef.current) {
      setSortMenuStyle(getMenuPosition(sortButtonRef.current, sortOptions.length));
    }

    setActiveSortIndex(nextActiveIndex);
    setIsSortMenuOpen(true);
  }, [onCloseFilters, selectedSortIndex, showFilters, sortOptions.length]);

  const handleFiltersButtonClick = React.useCallback(() => {
    closeSortMenu();
    onToggleFilters();
  }, [closeSortMenu, onToggleFilters]);

  const handleDraftCategoryChange = React.useCallback((nextCategory: ServiceCategoryId) => {
    setDraftCategory(nextCategory);

    if (nextCategory === 'college') {
      setDraftPriceRange('all');
    }
  }, []);

  const handleApplyFilters = React.useCallback(() => {
    if (!hasDraftChanges) {
      return;
    }

    onApplyFilters({
      category: draftCategory,
      priceRange: effectiveDraftPriceRange,
    });
    window.requestAnimationFrame(() => filtersButtonRef.current?.focus());
  }, [draftCategory, effectiveDraftPriceRange, hasDraftChanges, onApplyFilters]);

  const handleResetFilters = React.useCallback(() => {
    if (!hasDraftFilters && activeFilterCount === 0) {
      return;
    }

    setDraftCategory('all');
    setDraftPriceRange('all');
    onClearFilters();
    window.requestAnimationFrame(() => filtersButtonRef.current?.focus());
  }, [activeFilterCount, hasDraftFilters, onClearFilters]);

  const handleSortSelect = React.useCallback((option: ServiceSortOption) => {
    if (option.id !== sortBy) {
      onSortChange(option.id);
    }

    closeSortMenu(true);
  }, [closeSortMenu, onSortChange, sortBy]);

  const handleSortButtonKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openSortMenu(Math.min(sortOptions.length - 1, selectedSortIndex + 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      openSortMenu(Math.max(0, selectedSortIndex - 1));
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openSortMenu(selectedSortIndex);
    }
  }, [openSortMenu, selectedSortIndex, sortOptions.length]);

  const handleSortMenuKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeSortMenu(true);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSortIndex((currentIndex) => (currentIndex + 1) % sortOptions.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSortIndex((currentIndex) => (
        currentIndex - 1 + sortOptions.length
      ) % sortOptions.length);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      setActiveSortIndex(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      setActiveSortIndex(sortOptions.length - 1);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSortSelect(sortOptions[activeSortIndex]);
    }
  }, [activeSortIndex, closeSortMenu, handleSortSelect, sortOptions]);

  const handleFiltersPanelKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeFiltersAndFocusButton();
    }
  }, [closeFiltersAndFocusButton]);

  React.useLayoutEffect(() => {
    if (!isSortMenuOpen) {
      return undefined;
    }

    updateSortMenuPosition();

    window.addEventListener('resize', updateSortMenuPosition);

    return () => {
      window.removeEventListener('resize', updateSortMenuPosition);
    };
  }, [isSortMenuOpen, updateSortMenuPosition]);

  React.useLayoutEffect(() => {
    if (showFilters) {
      closeSortMenu();
      updateFiltersPanelPosition();
    }

    if (!showFilters) {
      setFiltersPanelStyle(undefined);
      return undefined;
    }

    window.addEventListener('resize', requestFiltersPanelPositionUpdate);
    window.addEventListener('scroll', requestFiltersPanelPositionUpdate, true);

    return () => {
      window.removeEventListener('resize', requestFiltersPanelPositionUpdate);
      window.removeEventListener('scroll', requestFiltersPanelPositionUpdate, true);

      if (filtersPositionFrameRef.current !== null) {
        window.cancelAnimationFrame(filtersPositionFrameRef.current);
        filtersPositionFrameRef.current = null;
      }
    };
  }, [closeSortMenu, requestFiltersPanelPositionUpdate, showFilters, updateFiltersPanelPosition]);

  React.useEffect(() => {
    if (!showFilters) {
      return;
    }

    setDraftCategory(selectedCategory);
    setDraftPriceRange(effectivePriceRange);
  }, [effectivePriceRange, selectedCategory, showFilters]);

  React.useEffect(() => {
    if (!isSortMenuOpen) {
      return undefined;
    }

    sortMenuRef.current?.focus();

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        sortButtonRef.current?.contains(target) ||
        sortMenuRef.current?.contains(target)
      ) {
        return;
      }

      closeSortMenu();
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [closeSortMenu, isSortMenuOpen]);

  React.useEffect(() => {
    if (!showFilters) {
      return undefined;
    }

    const focusFrame = window.requestAnimationFrame(() => {
      filtersPanelRef.current?.focus();
    });

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        filtersButtonRef.current?.contains(target) ||
        filtersPanelRef.current?.contains(target)
      ) {
        return;
      }

      closeFiltersAndFocusButton();
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [closeFiltersAndFocusButton, showFilters]);

  const sortMenu = isSortMenuOpen && typeof document !== 'undefined'
    ? createPortal(
      <div
        id={sortMenuId}
        ref={sortMenuRef}
        className={styles.sortMenu}
        style={sortMenuStyle}
        role="listbox"
        tabIndex={-1}
        aria-labelledby={sortButtonId}
        aria-activedescendant={`${sortMenuId}-${sortOptions[activeSortIndex]?.id || sortBy}`}
        onKeyDown={handleSortMenuKeyDown}
      >
        {sortOptions.map((option, optionIndex) => {
          const isSelected = option.id === sortBy;
          const isActive = optionIndex === activeSortIndex;

          return (
            <button
              key={option.id}
              id={`${sortMenuId}-${option.id}`}
              type="button"
              role="option"
              aria-selected={isSelected}
              tabIndex={-1}
              className={`${styles.sortMenuOption} ${isSelected ? styles.sortMenuOptionSelected : ''} ${isActive ? styles.sortMenuOptionActive : ''}`.trim()}
              onMouseEnter={() => setActiveSortIndex(optionIndex)}
              onClick={() => handleSortSelect(option)}
            >
              <span>{option.label}</span>
              {isSelected ? <span className={styles.sortMenuCheck} aria-hidden="true" /> : null}
            </button>
          );
        })}
      </div>,
      document.body
    )
    : null;

  const filtersPanel = showFilters && typeof document !== 'undefined'
    ? createPortal(
      <div
        id={filtersPanelId}
        ref={filtersPanelRef}
        className={styles.filtersPanel}
        style={filtersPanelStyle}
        role="dialog"
        tabIndex={-1}
        aria-labelledby={filtersPanelTitleId}
        onKeyDown={handleFiltersPanelKeyDown}
      >
        <div className={styles.filtersPanelHeader}>
          <h3 id={filtersPanelTitleId} className={styles.filtersPanelTitle}>Filters</h3>
          <button
            type="button"
            className={styles.filtersPanelClose}
            onClick={closeFiltersAndFocusButton}
            aria-label="Close filters"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div className={styles.filtersPanelBody}>
          <section className={styles.filtersSection} aria-labelledby={`${filtersPanelId}-category`}>
            <div id={`${filtersPanelId}-category`} className={styles.filtersSectionTitle}>
              Service type
            </div>
            <div className={styles.filterChoiceGrid} role="radiogroup" aria-label="Service type">
              {categories.map((category) => {
                const isSelected = category.id === draftCategory;

                return (
                  <label
                    key={category.id}
                    className={`${styles.filterChoice} ${isSelected ? styles.filterChoiceSelected : ''}`.trim()}
                  >
                    <input
                      className={styles.filterChoiceInput}
                      type="radio"
                      name={`${filtersPanelId}-category-choice`}
                      value={category.id}
                      checked={isSelected}
                      onChange={() => handleDraftCategoryChange(category.id)}
                    />
                    <span>{getCategoryLabel(category)}</span>
                    {isSelected ? <span className={styles.filterChoiceCheck} aria-hidden="true" /> : null}
                  </label>
                );
              })}
            </div>
          </section>

          <section className={styles.filtersSection} aria-labelledby={`${filtersPanelId}-price`}>
            <div className={styles.filtersSectionHeader}>
              <span id={`${filtersPanelId}-price`} className={styles.filtersSectionTitle}>
                Price
              </span>
              {draftCategory === 'college' ? (
                <span className={styles.filtersMutedLabel}>Not applied</span>
              ) : null}
            </div>
            <div className={styles.filterChoiceList} role="radiogroup" aria-label="Price range">
              {priceRangeOptions.map((option) => {
                const isPriceDisabled = draftCategory === 'college';
                const isSelected = option.id === effectiveDraftPriceRange;

                return (
                  <label
                    key={option.id}
                    className={`${styles.filterChoice} ${styles.filterChoiceRow} ${isSelected ? styles.filterChoiceSelected : ''} ${isPriceDisabled ? styles.filterChoiceDisabled : ''}`.trim()}
                  >
                    <input
                      className={styles.filterChoiceInput}
                      type="radio"
                      name={`${filtersPanelId}-price-choice`}
                      value={option.id}
                      checked={isSelected}
                      disabled={isPriceDisabled}
                      onChange={() => setDraftPriceRange(option.id)}
                    />
                    <span>{option.label}</span>
                    {isSelected ? <span className={styles.filterChoiceCheck} aria-hidden="true" /> : null}
                  </label>
                );
              })}
            </div>
          </section>
        </div>

        <div className={styles.filtersPanelActions}>
          <button
            type="button"
            className={`${styles.filtersPanelAction} ${styles.filtersPanelSecondaryAction}`.trim()}
            onClick={handleResetFilters}
            disabled={!hasDraftFilters && activeFilterCount === 0}
          >
            Reset
          </button>
          <button
            type="button"
            className={`${styles.filtersPanelAction} ${styles.filtersPanelPrimaryAction}`.trim()}
            onClick={handleApplyFilters}
            disabled={!hasDraftChanges}
          >
            Apply filters
          </button>
        </div>
      </div>,
      document.body
    )
    : null;

  return (
    <div className={styles.toolbar}>
      <div className={styles.toolbarHeading}>
        <h2 className={styles.toolbarTitle}>
          <span
            className={styles.toolbarHeadingIcon}
            style={toolbarIconStyle('service-fill.svg')}
            aria-hidden="true"
          />
          <span>Available Services & Opportunities</span>
        </h2>
        <p className={styles.toolbarMeta}>{resultSummary}</p>
      </div>

      <div className={styles.toolbarActions}>
        <button
          id={sortButtonId}
          ref={sortButtonRef}
          type="button"
          className={styles.sortControl}
          aria-haspopup="listbox"
          aria-expanded={isSortMenuOpen}
          aria-controls={isSortMenuOpen ? sortMenuId : undefined}
          onClick={() => (isSortMenuOpen ? closeSortMenu() : openSortMenu(selectedSortIndex))}
          onKeyDown={handleSortButtonKeyDown}
        >
          <span
            className={styles.sortControlIcon}
            style={toolbarIconStyle('sort-bold.svg')}
            aria-hidden="true"
          />
          <span className={styles.sortControlPrefix}>Sort by:</span>
          <span className={styles.sortValue}>{selectedSortOption?.label || 'Recommended'}</span>
          <span className={styles.sortChevron} aria-hidden="true">
            <SortChevronIcon />
          </span>
        </button>

        <button
          id={filtersButtonId}
          ref={filtersButtonRef}
          type="button"
          onClick={handleFiltersButtonClick}
          className={`${styles.filtersButton} ${showFilters || activeFilterCount > 0 ? styles.filtersButtonActive : ''}`.trim()}
          aria-haspopup="dialog"
          aria-expanded={showFilters}
          aria-controls={showFilters ? filtersPanelId : undefined}
        >
          <span
            className={styles.filtersButtonIcon}
            style={toolbarIconStyle('filter-bold.svg')}
            aria-hidden="true"
          />
          <span>Filters</span>
          {activeFilterCount > 0 ? (
            <span className={styles.filtersButtonBadge} aria-label={`${activeFilterCount} active filters`}>
              {activeFilterCount}
            </span>
          ) : null}
        </button>
      </div>

      {sortMenu}
      {filtersPanel}
    </div>
  );
};

export default React.memo(ServicesToolbar);
